import React from "react";
import foto from "../../assets/startbild/objekt.jpg";
import logo from "../../assets/startbild/muessig_logo_navy.png";
import { ACHSE, BILD_BREITE, BILD_HOEHE, KANTEN, KNOTEN, PUFFER } from "./netzplanGeometrie";
import "./StartbildNetzplan.css";

/**
 * Startbild «Netzplan» (24.09.2026; zum erneuten Abspielen /startbild-vorschau).
 *
 * Ablauf in zwei Saetzen: Auf dem Navy-Raster zeichnet sich ein Vorgangsknotennetz - Knoten
 * rasten ein, die Abhaengigkeitspfeile laufen nach, der kritische Weg leuchtet durch. Ein
 * Scan legt von links nach rechts die Treppe frei; unten links stehen Marke, Titel und
 * Prozesskette.
 *
 * Warum dieses Objekt: Ein Treppengelaender mit Handlauf, und vor allem eine grosse ruhige
 * Betonflaeche oben links - genau dort, wo die Szene liegt. Der Lauf steigt nach rechts an
 * und liest sich damit wie ein Terminplan.
 *
 * Warum ein negativer Puffer: Im Netzplan heisst das „schon zu spaet", und genau darum geht
 * es in dem Werkzeug. Die Farbe ist die des Werkzeugs selbst, dessen Pufferskala alles
 * <= 0 rot faerbt.
 *
 * Alles ist CSS-Animation (kein Zeitgeber, kein rAF): ein Durchlauf steht nach rund 3.2 s
 * und driftet dann nur noch, bis App.js `ausblenden` setzt - die Anwendung laedt darunter.
 * Das Netz wird ZWEIMAL gezeichnet: einmal auf der Planebene und einmal innerhalb des
 * Foto-Fensters. Sonst waere es nach dem Scan verdeckt.
 */

const SCHRITTE = ["Messen", "Systemplanung", "Freigabe", "Konstruktion", "Montage"];

type CssVars = React.CSSProperties & Record<`--${string}`, string | number>;

/** Das Netz. `ueberFoto` liegt im Foto-Fenster und bleibt nach dem Scan sichtbar. */
const Netz = ({ ueberFoto = false }: { ueberFoto?: boolean }) => (
    <svg
        className={`np-svg${ueberFoto ? " np-svg--real" : ""}`}
        viewBox={`0 0 ${BILD_BREITE} ${BILD_HOEHE}`}
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
    >
        {/* Zeitachse - der Netzplan rechnet in Kalenderwochen. */}
        <g className="np-achse">
            <path className="np-achse-linie" d={`M${ACHSE.x1} ${ACHSE.y} H${ACHSE.x2}`} pathLength={1} />
            {ACHSE.ticks.map((x) => (
                <path key={x} className="np-achse-tick" d={`M${x} ${ACHSE.y - 7} V${ACHSE.y + 7}`} />
            ))}
            {ACHSE.marken.map((m) => (
                <text key={m.text} className="np-achse-text" x={m.x} y={ACHSE.y - 16} textAnchor="middle">
                    {m.text}
                </text>
            ))}
        </g>

        {/* Abhaengigkeiten - laufen von Knoten zu Knoten nach. */}
        {KANTEN.map((k, i) => (
            <g key={k.id} className={`np-kante${k.kritisch ? " np-kante--kritisch" : ""}`}
               style={{ "--i": i } as CssVars}>
                <path className="np-kante-linie" d={k.d} pathLength={1} />
                <path className="np-kante-spitze" d={k.spitze} />
            </g>
        ))}

        {/* Vorgangsknoten - rasten nacheinander ein. */}
        {KNOTEN.map((n, i) => (
            <g key={n.id} className={`np-knoten${n.kritisch ? " np-knoten--kritisch" : ""}`}
               style={{ "--i": i } as CssVars}>
                <rect className="np-knoten-feld" x={n.x} y={n.y} width={n.w} height={n.h} rx={4} />
                <text className="np-knoten-dauer" x={n.x + n.w - 14} y={n.y + 19} textAnchor="end">{n.dauer}</text>
                <path className="np-knoten-kopf" d={`M${n.x} ${n.y + 27} H${n.x + n.w}`} />
                <path className="np-knoten-zeile" d={`M${n.x + 16} ${n.y + 47} H${n.x + n.w - 16}`} />
                <path className="np-knoten-zeile np-knoten-zeile--kurz" d={`M${n.x + 16} ${n.y + 63} H${n.x + n.w - 70}`} />
            </g>
        ))}

        {/* Der negative Puffer am unkritischen Zweig. */}
        <g className="np-puffer">
            <rect className="np-puffer-balken" x={PUFFER.x} y={PUFFER.y} width={PUFFER.w} height={PUFFER.h} rx={2} />
            <text className="np-puffer-text" x={PUFFER.textX} y={PUFFER.textY}>{PUFFER.text}</text>
        </g>
    </svg>
);

interface Props {
    /** Ablauf stauchen (< 1) oder dehnen (> 1). */
    tempo?: number;
    /** Weich ausblenden; App.js nimmt das Startbild danach ganz weg. */
    ausblenden?: boolean;
}

const StartbildNetzplan = ({ tempo = 1, ausblenden = false }: Props) => (
    <div
        className={`np-root${ausblenden ? " np-root--aus" : ""}`}
        style={{ "--tempo": tempo } as CssVars}
        role="status"
        aria-label="Netzplan wird geladen"
    >
        <div className="np-szene">
            <div className="np-plan" aria-hidden="true">
                <Netz />
            </div>

            {/* Aussen und innen verschieben sich gegenlaeufig: der Inhalt steht still, nur das
                Fenster wandert. Reine Transforms laufen auf dem Compositor - der Scan bleibt
                fluessig, waehrend die Anwendung darunter ihre Listen aufbaut. */}
            <div className="np-real">
                <div className="np-real-innen">
                    <img className="np-foto" src={foto} alt="" draggable={false} />
                    <Netz ueberFoto />
                </div>
            </div>

            <div className="np-scan" aria-hidden="true"><i /></div>
        </div>

        <div className="np-schleier" aria-hidden="true" />
        <div className="np-winkel" aria-hidden="true" />

        <div className="np-marke">
            <img className="np-logo" src={logo} alt="Müssig" draggable={false} />
            <h1 className="np-titel"><span>Netzplan</span></h1>
            <ol className="np-schritte">
                {SCHRITTE.map((s, i) => (
                    <li key={s} style={{ "--i": i } as CssVars}><i aria-hidden="true" />{s}</li>
                ))}
            </ol>
        </div>
    </div>
);

export default StartbildNetzplan;
